import React, { useState, useEffect } from 'react';
import { useAlert } from './CustomAlert/AlertContext';
import { fetchInterviewScreenshots } from '../hooks/useScreenshotCapture';
import { API_BASE } from '../utils/config';
import screenshotIcon from '../assets/screenshot.png';

/**
 * ScreenshotGallery - Displays interview screenshots grouped by role
 * 
 * @param {Object} props
 * @param {string} props.interviewId - The interview/room ID
 * @param {string} props.viewMode - 'grid' or 'timeline' (default: 'grid')
 * @param {boolean} props.showRoleFilters - Show all/candidate/interviewer tabs (default: true)
 */
const ScreenshotGallery = ({ interviewId, viewMode = 'grid', showRoleFilters = true }) => {
  const { showConfirm } = useAlert();

  const [screenshots, setScreenshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'candidate', 'interviewer'

  useEffect(() => {
    const loadScreenshots = async () => {
      if (!interviewId) return;
      
      try {
        setLoading(true);
        setError(null);
        const data = await fetchInterviewScreenshots(interviewId);
        setScreenshots(data.screenshots || []);
      } catch (e) {
        console.error('Failed to load screenshots:', e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    loadScreenshots();
  }, [interviewId]);

  // Group screenshots by role
  const groupedScreenshots = {
    candidate: screenshots.filter(s => s.role === 'candidate'),
    interviewer: screenshots.filter(s => s.role === 'interviewer'),
  };

  // Filter screenshots based on active tab when role filters are visible.
  const filteredScreenshots = !showRoleFilters
    ? screenshots
    : activeTab === 'all'
      ? screenshots
      : groupedScreenshots[activeTab] || [];

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Get full image URL
  const getImageUrl = (imagePath) => {
    if (imagePath.startsWith('http')) return imagePath;
    return `${API_BASE}${imagePath}`;
  };

  const handleDeleteScreenshot = async (screenshot, event) => {
    if (event) {
      event.stopPropagation();
    }

    if (!screenshot?.id) {
      setFeedback({ type: 'error', text: 'Unable to delete screenshot: missing id.' });
      return;
    }

    const confirmed = await showConfirm('Delete this screenshot? This action cannot be undone.');
    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(screenshot.id);
      setFeedback(null);

      const response = await fetch(`${API_BASE}/api/screenshots/${encodeURIComponent(screenshot.id)}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        let errorMessage = `Delete failed: ${response.status}`;
        try {
          const body = await response.json();
          if (body?.error) {
            errorMessage = body.error;
          }
        } catch (_) {
          // Ignore JSON parsing errors and use the default message.
        }
        throw new Error(errorMessage);
      }

      setScreenshots((prev) => prev.filter((item) => item.id !== screenshot.id));
      setSelectedImage((prev) => (prev?.id === screenshot.id ? null : prev));
      setFeedback({ type: 'success', text: 'Screenshot deleted successfully.' });
    } catch (e) {
      setFeedback({ type: 'error', text: e.message || 'Failed to delete screenshot.' });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <span>Loading screenshots...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <span style={styles.errorIcon}>⚠️</span>
        <span>Error loading screenshots: {error}</span>
      </div>
    );
  }

  if (screenshots.length === 0) {
    return (
      <div style={styles.emptyContainer}>
        <img src={screenshotIcon} alt="No screenshots" style={styles.emptyIcon} />
        <span>No screenshots captured for this interview</span>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header with tabs */}
      <div style={styles.header}>
        <h3 style={styles.title}>Interview Screenshots</h3>
        {showRoleFilters && (
          <div style={styles.tabContainer}>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === 'all' ? styles.activeTab : {}),
              }}
              onClick={() => setActiveTab('all')}
            >
              All ({screenshots.length})
            </button>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === 'candidate' ? styles.activeTab : {}),
              }}
              onClick={() => setActiveTab('candidate')}
            >
              Candidate ({groupedScreenshots.candidate.length})
            </button>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === 'interviewer' ? styles.activeTab : {}),
              }}
              onClick={() => setActiveTab('interviewer')}
            >
              Interviewer ({groupedScreenshots.interviewer.length})
            </button>
          </div>
        )}
      </div>

      {feedback && (
        <div
          style={{
            ...styles.feedback,
            ...(feedback.type === 'success' ? styles.feedbackSuccess : styles.feedbackError),
          }}
        >
          {feedback.text}
        </div>
      )}

      {/* Screenshots Grid/Timeline */}
      {viewMode === 'grid' ? (
        <div style={styles.grid}>
          {filteredScreenshots.map((screenshot, index) => (
            <div
              key={screenshot.id || index}
              style={styles.gridItem}
              onClick={() => setSelectedImage(screenshot)}
            >
              <img
                src={getImageUrl(screenshot.image_path)}
                alt={`${screenshot.role} screenshot`}
                style={styles.thumbnail}
                loading="lazy"
              />
              <div style={styles.gridItemOverlay}>
                <span style={{
                  ...styles.roleBadge,
                  backgroundColor: screenshot.role === 'candidate' ? '#10b981' : '#3b82f6',
                }}>
                  {screenshot.role}
                </span>
                <span style={styles.timestamp}>
                  {formatTimestamp(screenshot.created_at)}
                </span>
                <div style={styles.cardActions}>
                  <button
                    type="button"
                    style={styles.viewButton}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedImage(screenshot);
                    }}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    style={styles.deleteButton}
                    onClick={(event) => handleDeleteScreenshot(screenshot, event)}
                    disabled={deletingId === screenshot.id}
                  >
                    {deletingId === screenshot.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.timeline}>
          {filteredScreenshots.map((screenshot, index) => (
            <div key={screenshot.id || index} style={styles.timelineItem}>
              <div style={styles.timelineMarker}>
                <div style={{
                  ...styles.timelineDot,
                  backgroundColor: screenshot.role === 'candidate' ? '#10b981' : '#3b82f6',
                }} />
                {index < filteredScreenshots.length - 1 && <div style={styles.timelineLine} />}
              </div>
              <div style={styles.timelineContent} onClick={() => setSelectedImage(screenshot)}>
                <img
                  src={getImageUrl(screenshot.image_path)}
                  alt={`${screenshot.role} screenshot`}
                  style={styles.timelineThumbnail}
                  loading="lazy"
                />
                <div style={styles.timelineMeta}>
                  <span style={{
                    ...styles.roleBadge,
                    backgroundColor: screenshot.role === 'candidate' ? '#10b981' : '#3b82f6',
                  }}>
                    {screenshot.role}
                  </span>
                  <span style={styles.timelineTimestamp}>
                    {formatTimestamp(screenshot.created_at)}
                  </span>
                  <div style={styles.timelineActions}>
                    <button
                      type="button"
                      style={styles.viewButton}
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedImage(screenshot);
                      }}
                    >
                      View
                    </button>
                    <button
                      type="button"
                      style={styles.deleteButton}
                      onClick={(event) => handleDeleteScreenshot(screenshot, event)}
                      disabled={deletingId === screenshot.id}
                    >
                      {deletingId === screenshot.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox Modal */}
      {selectedImage && (
        <div style={styles.lightboxOverlay} onClick={() => setSelectedImage(null)}>
          <div style={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
            <button
              style={styles.lightboxClose}
              onClick={() => setSelectedImage(null)}
            >
              ✕
            </button>
            <img
              src={getImageUrl(selectedImage.image_path)}
              alt={`${selectedImage.role} screenshot`}
              style={styles.lightboxImage}
            />
            <div style={styles.lightboxMeta}>
              <span style={{
                ...styles.roleBadge,
                backgroundColor: selectedImage.role === 'candidate' ? '#10b981' : '#3b82f6',
                fontSize: '14px',
                padding: '6px 12px',
              }}>
                {selectedImage.role}
              </span>
              <span style={styles.lightboxTimestamp}>
                {formatTimestamp(selectedImage.created_at)}
              </span>
              <button
                type="button"
                style={styles.deleteButton}
                onClick={(event) => handleDeleteScreenshot(selectedImage, event)}
                disabled={deletingId === selectedImage.id}
              >
                {deletingId === selectedImage.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'var(--bg-card)',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: 'var(--shadow-card)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--text-primary)',
  },
  feedback: {
    borderRadius: '8px',
    padding: '10px 12px',
    marginBottom: '16px',
    fontSize: '14px',
    fontWeight: '500',
  },
  feedbackSuccess: {
    backgroundColor: '#ecfdf5',
    color: '#065f46',
    border: '1px solid #a7f3d0',
  },
  feedbackError: {
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    border: '1px solid #fecaca',
  },
  tabContainer: {
    display: 'flex',
    gap: '8px',
  },
  tab: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-card)',
    color: 'var(--text-muted)',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  activeTab: {
    backgroundColor: '#1e293b',
    color: 'white',
    borderColor: '#1e293b',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
    maxHeight: '500px',
    overflowY: 'auto',
    paddingRight: '8px',
  },
  gridItem: {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  thumbnail: {
    width: '100%',
    height: '150px',
    objectFit: 'cover',
    display: 'block',
  },
  gridItemOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
    padding: '24px 12px 12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
  },
  cardActions: {
    display: 'flex',
    gap: '8px',
    marginLeft: 'auto',
  },
  viewButton: {
    border: '1px solid rgba(255,255,255,0.7)',
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: '#ffffff',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  deleteButton: {
    border: '1px solid #ef4444',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  roleBadge: {
    fontSize: '11px',
    fontWeight: '600',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    textTransform: 'capitalize',
  },
  timestamp: {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.9)',
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    maxHeight: '500px',
    overflowY: 'auto',
    paddingRight: '8px',
  },
  timelineItem: {
    display: 'flex',
    gap: '16px',
  },
  timelineMarker: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '20px',
  },
  timelineDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  timelineLine: {
    width: '2px',
    flex: 1,
    backgroundColor: 'var(--border-color)',
    marginTop: '4px',
  },
  timelineContent: {
    flex: 1,
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    paddingBottom: '16px',
    cursor: 'pointer',
  },
  timelineThumbnail: {
    width: '120px',
    height: '80px',
    objectFit: 'cover',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  timelineMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  timelineActions: {
    display: 'flex',
    gap: '8px',
  },
  timelineTimestamp: {
    fontSize: '13px',
    color: 'var(--text-muted)',
  },
  lightboxOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  lightboxContent: {
    position: 'relative',
    maxWidth: '90vw',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  lightboxClose: {
    position: 'absolute',
    top: '-40px',
    right: '0',
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '8px',
  },
  lightboxImage: {
    maxWidth: '100%',
    maxHeight: 'calc(90vh - 80px)',
    objectFit: 'contain',
    borderRadius: '8px',
  },
  lightboxMeta: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    marginTop: '16px',
  },
  lightboxTimestamp: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '14px',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '40px',
    color: 'var(--text-muted)',
  },
  spinner: {
    width: '24px',
    height: '24px',
    border: '3px solid #e2e8f0',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '20px',
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    color: '#991b1b',
  },
  errorIcon: {
    fontSize: '18px',
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '40px',
    color: 'var(--text-muted)',
    backgroundColor: 'var(--bg-surface)',
    borderRadius: '12px',
  },
  emptyIcon: {
    width: '72px',
    height: '72px',
    objectFit: 'contain',
    opacity: 0.85,
  },
};

// Add keyframes for spinner animation
const styleSheet = document.createElement('style');
styleSheet.innerText = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .screenshot-grid-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
`;
document.head.appendChild(styleSheet);

export default ScreenshotGallery;
